import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, Pie, Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { Button } from 'react-bootstrap';
import HelpSite from "../../Common/HelpLink";
import Common from '../../Common/Common';
import ModuleService from "../../Setup/controller/ModuleController/ModuleController";
import ReportService from '../Controller/ReportController';
import DashboardService from '../Controller/DashboardController';
import InputParameters from './InputModal';
import ChartComponent from './ChartComponent';
import UserService from "../../Setup/controller/UserController/UserController";

const colorSchemes = {
  Default: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
  Pastel: ['#A8DADC', '#457B9D', '#F4A261', '#2A9D8F', '#E9C46A'],
  Monochrome: ['#333333', '#4F4F4F', '#666666', '#808080', '#999999'],
  Vibrant: ['#FF3E41', '#FF9F1C', '#FFB400', '#2EC4B6', '#E71D36'],
  Ocean: ['#1CA3EC', '#6EC1E4', '#0E749D', '#226789', '#84D7E1'],
  Autumn: ['#D2691E', '#FF7F50', '#FF4500', '#FF6347', '#FFA07A'],
  Neon: ['#FF00FF', '#00FFFF', '#FF4500', '#7FFF00', '#FFFF00'],
  Warm: ['#FF6F61', '#FF8C42', '#FFB100', '#E07B00', '#FFAD5A'],
};

function ChartDashboard() {
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [selectedChart, setSelectedChart] = useState('Bar');
  const [selectedColorScheme, setSelectedColorScheme] = useState('Default');
  const [selectedDiv, setSelectedDiv] = useState(null);
  const [selectedModule, setSelectedModule] = useState('Customers');
  const [showLabels, setShowLabels] = useState(true);
  const [ModuleList, setModuleList] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedReportID, setSelectedReportID] = useState(null);
  const [showInputModal, setShowInputModal] = useState(false);
  const [inputValues, setInputValues] = useState({});
  const [selectedReport, setSelectedReport] = useState({});
  const [users, setUsers] = useState([]);
  const [selectedUserID, setSelectedUserID] = useState(localStorage.getItem("UserID"));
  const [selectedRoleName, setRoleName] = useState(localStorage.getItem("RoleName"));
  const [modalClosed, setModalClosed] = useState(false);
  const [showApiModal, setShowApiModal] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [chartDataByDiv, setChartDataByDiv] = useState({
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: []
  });
  const [chartConfigs, setChartConfigs] = useState({
    1: { chartType: 'Table', colorScheme: 'Default', module: 'Lead', showLabels: true, reportName: '' },
    2: { chartType: 'Table', colorScheme: 'Default', module: 'Assets', showLabels: true, reportName: '' },
    3: { chartType: 'Table', colorScheme: 'Default', module: 'Jobs', showLabels: true, reportName: '' },
    4: { chartType: 'Table', colorScheme: 'Ocean', module: 'SalesOrders', showLabels: true, reportName: '' },
    5: { chartType: 'Pie', colorScheme: 'Vibrant', module: 'Extra1', showLabels: true, reportName: '' },
    6: { chartType: 'Line', colorScheme: 'Pastel', module: 'Extra2', showLabels: true, reportName: '' },
  });

  const chartStyles = { width: '250px', height: '250px' };

  const chartData = (colorScheme, dataset) => ({
    labels: dataset.map((item) => item.label),
    datasets: [
      {
        data: dataset.map((item) => item.value),
        backgroundColor: colorSchemes[colorScheme],
      },
    ],
  });

  const handleDivClick = (divNumber) => {
    setSelectedDiv(divNumber);
    const config = chartConfigs[divNumber];
    setSelectedChart(config.chartType);
    setSelectedColorScheme(config.colorScheme);
    setSelectedModule(config.module);
    setShowLabels(config.showLabels);
    setShowModal(true);
  };

  const handleInputChange = (paramName, value) => {
    setInputValues((prevValues) => ({
      ...prevValues,
      [paramName]: value,
    }));
  };

  const handleExecuteReport = async (updatedInputValues) => {
    if (!updatedInputValues || typeof updatedInputValues !== 'object') return;

    if (selectedReportID && selectedDiv) {
      try {
        const output = await ReportService.executeReport(selectedReportID, updatedInputValues);

        // Ensure we pass array to chart
        const resultsArray = Array.isArray(output.results[0])
          ? output.results[0]
          : output.results[0]?.data || [];

        setChartDataByDiv((prevData) => ({
          ...prevData,
          [selectedDiv]: resultsArray,
        }));

        setShowInputModal(false);
        setShowModal(false);
      } catch (err) {
        console.error('Failed to execute report:', err);
      }
    }
  };
  const handleCloseModal = async () => {

    setShowModal(false);
    setModalClosed(prev => !prev);
  }
  const updateChartForDiv = async () => {
    // Update the chartConfigs first
    const updatedConfigs = {
      ...chartConfigs,
      [selectedDiv]: {
        ...chartConfigs[selectedDiv],
        chartType: selectedChart,
        colorScheme: selectedColorScheme,
        module: selectedModule,
        showLabels: showLabels,
        reportName: selectedReport?.Name || chartConfigs[selectedDiv].reportName,
        reportID: selectedReportID,
      },
    };

    setChartConfigs(updatedConfigs);

    // Execute the report to refresh data
    await handleExecuteReport(inputValues);

    // Prepare divs + chart configs for saving
    const divs = {
      Div1: updatedConfigs[1]?.ReportID || null,
      Div2: updatedConfigs[2]?.ReportID || null,
      Div3: updatedConfigs[3]?.ReportID || null,
      Div4: updatedConfigs[4]?.ReportID || null,
      Div5: updatedConfigs[5]?.ReportID || null,
      Div6: updatedConfigs[6]?.ReportID || null,
      chart1Config: updatedConfigs[1],
      chart2Config: updatedConfigs[2],
      chart3Config: updatedConfigs[3],
      chart4Config: updatedConfigs[4],
      chart5Config: updatedConfigs[5],
      chart6Config: updatedConfigs[6],
    };

    console.log("💾 Saving Dashboard Config:", divs);

    await DashboardService.saveDashboardConfig(selectedUserID, divs);
    setShowModal(false);
  };


  const fetchModules = async () => {
    try {
      const redirectCallback = () => navigate('/');
      const data = await ModuleService.GetAllModules(redirectCallback, "en");
      setModuleList(data);
    } catch (err) {
      console.error('Failed to load modules', err);
    }
  };
  /* const fetchDashboardReports = async (UserID) => {
     try {
       const data = await DashboardService.DashboardReport(UserID);
       debugger;
       setChartDataByDiv({
         1: data.chart1?.data || [],
         2: data.chart2?.data || [],
         3: data.chart3?.data || [],
         4: data.chart4?.data || [],
         5: data.chart5?.data || [],
         6: data.chart6?.data || [],
       });
 
       setChartConfigs((prevConfigs) => ({
         1: { ...prevConfigs[1], reportName: data.chart1?.reportName || 'No Report Selected', ReportID: data.chart1?.ReportID },
         2: { ...prevConfigs[2], reportName: data.chart2?.reportName || 'No Report Selected', ReportID: data.chart2?.ReportID },
         3: { ...prevConfigs[3], reportName: data.chart3?.reportName || 'No Report Selected', ReportID: data.chart3?.ReportID },
         4: { ...prevConfigs[4], reportName: data.chart4?.reportName || 'No Report Selected', ReportID: data.chart4?.ReportID },
         5: { ...prevConfigs[5], reportName: data.chart5?.reportName || 'No Report Selected', ReportID: data.chart5?.ReportID },
         6: { ...prevConfigs[6], reportName: data.chart6?.reportName || 'No Report Selected', ReportID: data.chart6?.ReportID },
       }));
     } catch (err) {
       console.error('Failed to load dashboard reports', err);
     }
   };*/

  const fetchDashboardReports = async (UserID) => {
    try {
      const data = await DashboardService.DashboardReport(UserID);
      const dashboardSections = await DashboardService.GetDashboardSections(UserID);
      setTotalSections(dashboardSections[0].Sections || 6);

      const newChartData = {};
      const newChartConfigs = {};

      for (let i = 1; i <= 6; i++) {
        const chart = data[`chart${i}`] || {};
        newChartData[i] = chart.data || [];
        newChartConfigs[i] = {
          chartType: chart.chartType || 'Table',
          colorScheme: chart.colorScheme || 'Default',
          module: chart.module || '',
          showLabels: chart.showLabels ?? true,
          reportName: chart.reportName || 'No Report Selected',
          ReportID: chart.ReportID || null,
        };
      }

      setChartDataByDiv(newChartData);
      setChartConfigs(newChartConfigs);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersData = await UserService.getAllUsers(() => navigate('/'));
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleReportSelect = async (reportId) => {
    try {
      const details = await ReportService.getReportDetail(reportId);
      setSelectedReport(details);

      const clientID = localStorage.getItem('CompanyID');
      const userID = selectedUserID;
      setInputValues({ ClientID: clientID, UserID: userID });

      if (selectedDiv) {
        setChartConfigs((prevConfigs) => ({
          ...prevConfigs,
          [selectedDiv]: {
            ...prevConfigs[selectedDiv],
            reportName: details.report[0]?.Name || 'No Report Selected',
            ReportID: details.report[0]?.ID || '0',
          },
        }));
      }

      if (details.inputParams[0].length === 0) {
        await handleExecuteReport({ ClientID: clientID, UserID: userID });
      } else {
        setShowInputModal(true);
      }
    } catch (err) {
      console.error('Failed to load report details', err);
    }
  };

  useEffect(() => {
    fetchModules();
    fetchUsers();
    if (selectedUserID) fetchDashboardReports(selectedUserID);
  }, [selectedUserID, modalClosed]);
  const fetchReports = async (moduleID) => {
    try {
      const data = await DashboardService.getDashboardReports(moduleID);
      setReports(data);
    } catch (err) {
      //setError('Failed to load reports');
      console.error(err);
    }
  };
  const [totalSections, setTotalSections] = useState(6);
  const visibleDivs = Object.keys(chartConfigs)
    .map(Number)
    .filter(
      (divNum) =>
        chartConfigs[divNum]?.ReportID && chartConfigs[divNum]?.ReportID !== 0
    );

  // 2️⃣ Decide column layout
  const getColumnClass = (count) => {
    if (count === 4) return "col-xl-6 col-lg-6 col-md-6"; // 2 above + 2 below
    if (count >= 3) return "col-xl-4 col-lg-4 col-md-6"; // 3 per row
    if (count === 2) return "col-xl-6 col-lg-6 col-md-6";
    return "col-xl-12 col-lg-12 col-md-12";
  };

  const columnClass = getColumnClass(visibleDivs.length);

  const handleCheckApi = async () => {
    debugger;
    const ApiData = await DashboardService.GetApiDetail();
    setApiData(ApiData);
    setShowApiModal(true);

  };


  return (
    <div>
      <Common ModuleName="Dashboard"
        HelpFormName="Dashboard" HelpModule="2" />

      <div className="main-content-header">
        <div className="row">
          <div className="col-xl-7"><h1>Dashboard</h1></div>
          <div className="col-xl-3" style={{ textAlign: 'right' }}>
            {selectedRoleName === "Administrator" && (
              <select
                className="form-control"
                value={selectedUserID}
                onChange={(e) => {
                  const newUserID = e.target.value;
                  setSelectedUserID(newUserID);
                  fetchDashboardReports(newUserID);
                }}
              >
                {users.map((user) => (
                  <option key={user.UserID} value={user.UserID}>{user.DisplayName}</option>
                ))}
              </select>
            )}
          </div>

          <div className="col-xl-2" style={{ textAlign: 'right' }}>
            <select
              className="form-control"
              value={totalSections}
              onChange={(e) => {
                const value = Number(e.target.value);
                setTotalSections(value);
                DashboardService.saveDashboardSections(selectedUserID, value);
              }}
            >
              <option value={3}>3 Sections</option>
              <option value={4}>4 Sections</option>
              <option value={6}>6 Sections</option>
            </select>

          </div>
          
        </div>
      </div>

      <div className="row">
        <div className="col-xl-12">
          <div className="card mb-30">
            <div className="card-body">
              <div className="row" style={{ marginLeft: 0, marginRight: 0 }}>
                {(() => {
                  // 1️⃣ Get configured sections
                  const configuredDivs = [1, 2, 3, 4, 5, 6].filter(
                    (divNum) =>
                      chartConfigs[divNum]?.ReportID &&
                      chartConfigs[divNum]?.ReportID !== 0
                  );

                  // 2️⃣ Default to all 6 if nothing configured
                  const visibleDivs = Array.from({ length: totalSections }, (_, i) => i + 1);

                  // 3️⃣ Decide layout
                  const getColumnClass = (count) => {
                    if (count === 4) return "col-xl-6 col-lg-6 col-md-6";
                    if (count >= 3) return "col-xl-4 col-lg-4 col-md-6";
                    if (count === 2) return "col-xl-6 col-lg-6 col-md-6";
                    return "col-xl-12 col-lg-12 col-md-12";
                  };

                  const columnClass = getColumnClass(visibleDivs.length);

                  // 4️⃣ Render charts
                  return visibleDivs.map((divNum) => (
                    <div key={divNum} className={columnClass}>
                      <ChartComponent
                        array1={chartDataByDiv[divNum] || []}
                        chartType={chartConfigs[divNum]?.chartType || 'Pie'}
                        colorScheme={colorSchemes[chartConfigs[divNum]?.colorScheme || 'Default']}
                        handleDivClick={handleDivClick}
                        divNum={divNum}
                        chartStyles={chartStyles}
                        reportName={chartConfigs[divNum]?.reportName || 'No Report Selected'}
                        onLeadModalClose={handleCloseModal}
                      />
                    </div>
                  ));
                })()}
              </div>

            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Configure Chart for Div {selectedDiv}</h5>
                <button type="button" className="close" onClick={() => setShowModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {/* Chart Type */}
                <div className="row mb-3" style={{ marginTop: "10px" }}>
                  <label className="col-sm-3 col-form-label" style={{ marginLeft: "10px" }}>Chart Type</label>
                  <div className="col-sm-8">
                    <select className="form-control" value={selectedChart} onChange={(e) => setSelectedChart(e.target.value)}>
                      <option value="Bar">Bar Chart</option>
                      <option value="Pie">Pie Chart</option>
                      <option value="Line">Line Chart</option>
                      <option value="Table">Table View</option>
                    </select>
                  </div>
                </div>
                {/* Color Scheme */}
                <div className="row mb-3">
                  <label className="col-sm-3 col-form-label" style={{ marginLeft: "10px" }}>Color Scheme</label>
                  <div className="col-sm-8">
                    <select className="form-control" value={selectedColorScheme} onChange={(e) => setSelectedColorScheme(e.target.value)}>
                      {Object.keys(colorSchemes).map((scheme) => <option key={scheme} value={scheme}>{scheme}</option>)}
                    </select>
                  </div>
                </div>
                {/* Module */}
                <div className="row mb-3">
                  <label className="col-sm-3 col-form-label" style={{ marginLeft: "10px" }}>Select Module</label>
                  <div className="col-sm-8">
                    <select className="form-control" value={selectedModule} onChange={(e) => {
                      setSelectedModule(e.target.value);
                      fetchReports(e.target.value);
                    }}>
                      <option value="0">Select Module</option>
                      {ModuleList.map((module) => (
                        <option key={module.PK_SearchModule_ID} value={module.PK_SearchModule_ID}>{module.vc_ModuleName}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Report */}
                <div className="row mb-3">
                  <label className="col-sm-3 col-form-label" style={{ marginLeft: "10px" }}>Select Report</label>
                  <div className="col-sm-8">
                    <select className="form-control" value={selectedReportID || 0} onChange={(e) => {
                      setSelectedReportID(e.target.value);
                      handleReportSelect(e.target.value);
                    }}>
                      <option value="0">Select Report</option>
                      {reports.map((report) => <option key={report.ID} value={report.ID}>{report.Name}</option>)}
                    </select>
                  </div>
                </div>
                {/* Show Labels */}
                <div className="row mb-3">
                  <label className="col-sm-3 col-form-label" style={{ marginLeft: "10px" }}>Show Label</label>
                  <div className="col-sm-8">
                    <input type="checkbox" checked={showLabels} onChange={() => setShowLabels(!showLabels)} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={updateChartForDiv}>Update Chart</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInputModal && (
        <InputParameters
          inputParams={selectedReport.inputParams?.[0] || []}
          inputValues={inputValues}
          onInputChange={handleInputChange}
          onExecuteReport={handleExecuteReport}
          onClose={() => setShowInputModal(false)}
        />
      )}
      {showApiModal && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">
                  API Response
                </h5>

                <button
                  type="button"
                  className="close"
                  onClick={() => setShowApiModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>

              <div className="modal-body">
                <pre
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "15px",
                    borderRadius: "5px",
                    maxHeight: "500px",
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word"
                  }}
                >
                  {JSON.stringify(apiData, null, 2)}
                </pre>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowApiModal(false)}
                >
                  Close
                </button>
              </div>

            </div>
          </div>

          {/* Modal backdrop */}
          <div
            className="modal-backdrop fade show"
            onClick={() => setShowApiModal(false)}
          ></div>
        </div>
      )}
    </div>
  );
}

export default ChartDashboard;
